import { HttpInterceptorFn } from '@angular/common/http';

import * as Sentry from '@sentry/angular';

import { throwError } from 'rxjs';

import { formDecode } from '../input-safety/form-decode';
import {
  UnsafeInputIssue,
  buildUnsafeInputRejection,
  unsafeKeyIssue,
  unsafeValueIssue,
} from '../input-safety/unsafe-input-rejection';
import { UnsafeMarkupRule, isUnsafeMarkup } from '../input-safety/unsafe-markup';

const BROWSER_STRIPPED_CHARACTERS = /[\t\n\r]/g;
const ASCII_UPPERCASE = /[A-Z]/g;
const URL_ORIGIN = /^(?:[a-z][\d+.a-z-]*:)?\/\/[^#/?]*/i;
const URL_QUERY_OR_FRAGMENT = /[#?]/;
const UNSAFE_KEYS = Symbol('unsafe-keys');
const REJECTION_LOG_MESSAGE = 'Query input rejected as unsafe';

interface InspectedPair {
  group: ParameterGroup;
  issue: UnsafeInputIssue;
  rule: UnsafeMarkupRule | null;
}

type ParameterGroup = string | typeof UNSAFE_KEYS;

interface UnsafeParameter {
  issue: UnsafeInputIssue;
  rule: UnsafeMarkupRule;
}

/**
 * Fails every request whose query string holds a key or value the input-safety rule flags as unsafe markup, before it
 * reaches the network. It is the innermost interceptor, so it sees the final URL. A safe request passes unchanged.
 * Each rejection emits one Sentry warning log, which never holds the term.
 *
 * @param req The outgoing request.
 * @param next The rest of the chain.
 * @returns The chain's response for a safe request, or a stream that fails with the 400 rejection.
 */
export const inputSafetyInterceptor: HttpInterceptorFn = (req, next) => {
  const unsafeParameters = inspectQuery(req.urlWithParams);
  if (unsafeParameters.length === 0) {
    return next(req);
  }

  return throwError(() => {
    logRejection(req.url, unsafeParameters);
    return buildUnsafeInputRejection(
      req.url,
      unsafeParameters.map(({ issue }) => issue),
    );
  });
};

/**
 * Reads the path of a request URL, so a log can name the endpoint without the term.
 *
 * @param url The request URL, relative or absolute.
 * @returns The URL without its origin, query and fragment.
 */
function apiPathOf(url: string): string {
  return url.replace(URL_ORIGIN, '').split(URL_QUERY_OR_FRAGMENT, 1)[0];
}

/**
 * Checks one `key=value` pair.
 *
 * An unsafe key is never named, so every unsafe key belongs to the one shared group, and the value of such a pair
 * is not looked at. Any other key groups with the keys that differ from it only in ASCII case.
 *
 * @param pair The raw pair, never empty.
 * @returns The group the pair belongs to, the entry that describes it if it is unsafe, and the rule it breaks, if any.
 */
function inspectPair(pair: string): InspectedPair {
  const separator = pair.indexOf('=');
  const key = formDecode(separator === -1 ? pair : pair.slice(0, separator));
  const keyRule = isUnsafeMarkup(key);

  if (keyRule !== null) {
    return { group: UNSAFE_KEYS, issue: unsafeKeyIssue(), rule: keyRule };
  }

  const value = separator === -1 ? '' : formDecode(pair.slice(separator + 1));

  return {
    group: key.replaceAll(ASCII_UPPERCASE, (letter) => letter.toLowerCase()),
    issue: unsafeValueIssue(key),
    rule: isUnsafeMarkup(value),
  };
}

/**
 * Reads the query the way a browser sends it and the server parses it, and lists the offending parameters.
 *
 * A browser drops every tab, line feed and carriage return from a URL and never sends the fragment. Each key and
 * value is decoded once, as the server's query parser does. Keys that differ only in ASCII case are one parameter,
 * which shapes this list only, never the decision. A parameter keeps the rule of its first unsafe appearance.
 *
 * @param urlWithParams The request URL with its parameters.
 * @returns One entry per offending parameter in order of first appearance; empty for a safe query.
 */
function inspectQuery(urlWithParams: string): UnsafeParameter[] {
  const parameters = new Map<ParameterGroup, Pick<InspectedPair, 'issue' | 'rule'>>();

  for (const pair of queryOf(urlWithParams).split('&')) {
    if (pair === '') {
      continue;
    }

    const inspected = inspectPair(pair);
    const known = parameters.get(inspected.group);
    parameters.set(inspected.group, {
      issue: known?.issue ?? inspected.issue,
      rule: known?.rule ?? inspected.rule,
    });
  }

  return [...parameters.values()].filter((parameter): parameter is UnsafeParameter => parameter.rule !== null);
}

/**
 * Emits the warning log that makes over-blocking visible, because a rejected request never reaches Kt.Api. It
 * carries the endpoint path, the first entry's name and rule, and the entry count; never a key or value.
 *
 * @param url The request URL.
 * @param unsafeParameters The offending parameters, never empty.
 */
function logRejection(url: string, unsafeParameters: readonly UnsafeParameter[]): void {
  const [first] = unsafeParameters;

  Sentry.logger.warn(REJECTION_LOG_MESSAGE, {
    'input_safety.api_path': apiPathOf(url),
    'input_safety.parameter': first.issue.propertyName,
    'input_safety.rule': first.rule,
    'input_safety.unsafe_count': unsafeParameters.length,
  });
}

/**
 * Takes the raw query string out of a URL.
 *
 * @param urlWithParams The request URL with its parameters.
 * @returns The text between the first `?` and the fragment, with the characters a browser drops removed.
 */
function queryOf(urlWithParams: string): string {
  const sent = urlWithParams.replaceAll(BROWSER_STRIPPED_CHARACTERS, '');
  const beforeFragment = sent.split('#', 1)[0];
  const queryStart = beforeFragment.indexOf('?');

  return queryStart === -1 ? '' : beforeFragment.slice(queryStart + 1);
}
